import { createAction, props } from '@ngrx/store';
import { ComplianceReportTab } from 'src/app/compliance/compliance-report-new/shared/enums/compliance-report-tab.enum';
import { AutoMatchRequirementsDto } from '../../api/models/auto-match-requirements-dto.model';
import { ComplianceReportDto } from '../../api/models/compliance-report-dto.model';
import { ComplianceReportPermissionsDto } from '../../api/models/compliance-report-permissions-dto.model';

import { ReadinessStatusDto } from '../../api/models/readiness-status-dto.model';
import { RequirementDto } from '../../api/models/requirement-dto.model';
import { StatusDto } from '../../api/models/status-dto.model';
import { ReadinessStatus } from '../../models/compliance-report-models';
import { ComplianceStatus } from '../../models/compliance-status';
import { ComplianceStatusDto } from '../../api/models/compliance-status-dto.model';
import { JobComplianceRequirementChanges } from '../../job-requirements/job-requirements.service';
import { JobRequirementChangesDto } from '../../api/models/job-requirement-changes-dto.model';
import { ComplianceReportRequirementNoteDto } from '../../api/models/compliance-report-requirement-note-dto.model';
import { ComplianceStatusEnum } from '../../api/enums/compliance-status-enum.enum';
import { AutoMatchSignalRequirementsResponseDto } from '../../models/automatch-signal-requirements-response.model';
import { UpdateComplianceReportStatusRequest } from '../../api/models/update-compliance-report-status-request.model';
import { SpecialRulesAutoMatchRequestDto } from '../../api/models/special-rules-auto-match-request-dto.model';
import { SpecialRulesRequirementDto } from '../../api/models/special-rules-requirement-dto.model';
import { DocumentForComplianceReportDto } from 'src/app/compliance/api/models/document-for-compliance-report-dto.model';

// REQUEST ACTIONS
export const getCurrentContract = createAction(
    '[Compliance Report Component] Get Current Contract',
    props<{ contractId: number }>()
);

export const getCurrentPermissions = createAction('[Compliance Report Component] Get Current Permissions');

export const updateRequirementsForCurrentContract = createAction(
    '[Compliance Report Component] Update Requirements For Current Contract',
    props<{ contractId: number }>()
);

export const updateAyaClearedStatusForCurrentContract = createAction(
    '[Compliance Report Component] Update Aya Cleared Status For Current Contract',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);
export const updateAyaClearedStatusForCurrentContractSuccess = createAction(
    '[Compliance Report Component] Update Aya Cleared Status For Current Success',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);
export const updateAyaClearedStatusForCurrentContractFailure = createAction(
    '[Compliance Report Component] Update Aya Cleared Status For Current Fail',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);

export const updateClientClearedStatusForCurrentContract = createAction(
    '[Compliance Report Component] Update Client Cleared Status For Current Contract',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);
export const updateClientClearedStatusForCurrentContractSuccess = createAction(
    '[Compliance Report Component] Update Client Cleared Status For Current Success',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);
export const updateClientClearedStatusForCurrentContractFailure = createAction(
    '[Compliance Report Component] Update Client Cleared Status For Current Fail',
    props<{ contractId: number; status: UpdateComplianceReportStatusRequest }>()
);

export const updateComplianceSpecialistName = createAction(
    '[Compliance Report Component] Update Compliance Specialist',
    props<{ complianceSpecialistName: string }>()
);

export const updateCredentialingSpecialistName = createAction(
    '[Compliance Report Component] Update Credentialing Specialist',
    props<{ credentialingSpecialistName: string }>()
);

export const getAvailableStatuses = createAction('[Compliance Report Component] Get Available Statuses');

export const getReadinessStatuses = createAction('[Compliance Report Component] Get Readiness Statuses');

export const markRequirementAsModified = createAction(
    '[Compliance Report Component] Mark Requirement As Modified',
    props<{
        requirement: RequirementDto;
        isRequired: boolean;
    }>()
);

export const processSpecialRules = createAction(
    '[Compliance Report Component] Process Special Rules For requirements',
    props<{ specialRulesAutoMatchRequestDto: SpecialRulesAutoMatchRequestDto }>()
);

export const markCandidateDocumentAsModified = createAction(
    '[Compliance Report Component] Mark Candidate Document As Modified',
    props<{
        requirement: RequirementDto;
        candidateDocument: DocumentForComplianceReportDto;
        isSelected: boolean;
    }>()
);

export const updateModifiedRequirement = createAction(
    '[Compliance Report Component] Update Modified Requirement',
    props<{ requirement: RequirementDto }>()
);

export const updateComplianceNoteForRequirement = createAction(
    '[Compliance Report Component] Update Compliance Note For Requirement',
    props<{
        contractId: number;
        documentTypeId: number;
        requirementId: number;
        complianceNote: string;
        addOrEdit: 'Add' | 'Edit';
    }>()
);

export const updateCredentialingNoteForRequirement = createAction(
    '[Compliance Report Component] Update Credentialing Note For Requirement',
    props<{ requirementId: number; credentialingNote: string }>()
);

export const qualifyRequirements = createAction(
    '[Compliance Report Component] Qualify Requirements',
    props<{
        contractId: number;
        requirements: RequirementDto[];
        modifiedRequirements: RequirementDto[];
        automatchData?: AutoMatchRequirementsDto;
    }>()
);

export const saveRequirements = createAction(
    '[Compliance Report Component] Save Requirements',
    props<{
        contractId: number;
        oldRequirements: RequirementDto[];
        requirements: RequirementDto[];
    }>()
);

export const updateComplianceReportNote = createAction(
    '[Compliance Report Component] Update Compliance Report Note',
    props<{ contractId: number; complianceReportNote: string }>()
);

export const getAutomatchData = createAction(
    '[Compliance Report Component] Get Automatch Data',
    props<{ contractId: number; automatchRequestData: AutoMatchRequirementsDto; isQualified: boolean }>()
);

export const saveSelectedCandidateDocument = createAction(
    '[Compliance Report Component] Save Selected Candidate Document',
    props<{
        contractId: number;
        candidateDocument: DocumentForComplianceReportDto;
        requirement: RequirementDto;
        isSelected: boolean;
        isQualified: boolean;
    }>()
);

export const markAsReviewed = createAction(
    '[Compliance Report Component] Mark As Reviewed',
    props<{ contractId: number }>()
);

// STORE UPDATE ACTIONS
export const updateCurrentComplianceReportTab = createAction(
    '[Compliance Report Component] Update Current Compliance Report Tab',
    props<{ tab: ComplianceReportTab }>()
);

export const updateSelectedDocument = createAction(
    '[Compliance Report Component] Update Selected Document',
    props<{ requirementId: number; selectedDocument: DocumentForComplianceReportDto; selected: boolean }>()
);

export const addSelectedCandidateDocument = createAction(
    '[Compliance Report Component] Update Candidate Document Master Files',
    props<{ selectedCandidateDocumentId: number }>()
);

export const bulkUpdateSelectedCandidateDocuments = createAction(
    '[Compliance Report Component] Bulk Update Selected Documents',
    props<{ selectedCandidateDocumentIds: number[] }>()
);

export const removeSelectedCandidateDocument = createAction(
    '[Compliance Report Component] Remove Candidate Document Master Files',
    props<{ selectedCandidateDocumentId: number }>()
);

export const updateModifiedRequirements = createAction(
    '[Compliance Report Component] Update Modified Requirements',
    props<{ modifiedRequirements: RequirementDto[] }>()
);

export const updateManualReviewItems = createAction(
    '[Compliance Report Component] Update Manual Review Items',
    props<{ manualReviewItems: boolean }>()
);

export const updateManualQualifications = createAction(
    '[Compliance Report Component] Update Manual Qualifications',
    props<{ manualQualifications: boolean }>()
);

export const getJobRequirementsChanges = createAction(
    '[Compliance Report Component] Get Job Requirements Changes',
    props<{ jobId: number; contractId: number }>()
);

export const updateContractComplianceStatus = createAction(
    '[Compliance Report Component] Update Contract Compliance Status',
    props<{ complianceStatus: ComplianceStatusEnum }>()
);

export const addToManuallySelectedCandidateDocuments = createAction(
    '[Compliance Report Component] Add Id To Manually Selected Candidate Documents',
    props<{ candidateDocument: DocumentForComplianceReportDto; documentTypeId: number }>()
);

export const addIdToManuallyDeselectedCandidateDocuments = createAction(
    '[Compliance Report Component] Add Id To Manually Deselected Candidate Documents',
    props<{ candidateDocument: DocumentForComplianceReportDto; documentTypeId: number }>()
);

export const updateCurrentComplianceStatus = createAction(
    '[Compliance Report Component] Update Compliance Status',
    props<{ complianceStatus: ComplianceStatusEnum }>()
);

// SUCCESS ACTIONS
export const getCurrentContractSuccess = createAction(
    '[Compliance Report Effects] Get Current Contract Success',
    props<{ currentContract: ComplianceReportDto }>()
);

export const processSpecialRulesSuccess = createAction(
    '[Compliance Report Effects] process special rules on Success',
    props<{ SpecialRulesRequirementDto: SpecialRulesRequirementDto[] }>()
);

export const getCurrentPermissionsSuccess = createAction(
    '[Compliance Report Effects] Get Current Permissions Success',
    props<{ permissions: ComplianceReportPermissionsDto }>()
);

export const updateRequirementsForCurrentContractSuccess = createAction(
    '[Compliance Report Effects] Update Requirements For Current Contract Success',
    props<{ requirements: RequirementDto[] }>()
);

export const getAvailableStatusesSuccess = createAction(
    '[Compliance Report Effects] Get Available Statuses Success',
    props<{ availableStatuses: ComplianceStatus[] | StatusDto[] }>()
);

export const getReadinessStatusesSuccess = createAction(
    '[Compliance Report Effects] Get Readiness Statuses Success',
    props<{ readinessStatuses: ReadinessStatus[] | ReadinessStatusDto[] }>()
);

export const qualifyRequirementsSuccess = createAction('[Compliance Report Effects] Qualify Requirements Success');

export const saveRequirementsSuccess = createAction('[Compliance Report Effects] Save Requirements Success');

export const updateComplianceNoteForRequirementSuccess = createAction(
    '[Compliance Report Effects] Update Compliance Note For Requirement Success',
    props<{
        requirementId: number;
        documentTypeId: number;
        savedComplianceNote: ComplianceReportRequirementNoteDto;
        addOrEdit: 'Add' | 'Edit';
    }>()
);

export const updateComplianceReportNoteSuccess = createAction(
    '[Compliance Report Effects] Update Compliance Report Note Success',
    props<{ contractId: number; note: string }>()
);

export const getAutomatchDataSuccess = createAction(
    '[Compliance Report Effects] Get Automatch Data Success',
    props<{ automatchData: AutoMatchRequirementsDto }>()
);

export const automatchSignalReceived = createAction(
    '[Compliance Report Effects] Automatch Signal Received',
    props<{ automatchData: AutoMatchSignalRequirementsResponseDto }>()
);

export const setSingleSignalAutomatchSuccess = createAction(
    '[Compliance Report Effects] Set Single SignalR Automatch Success',
    props<{ automatchData: AutoMatchSignalRequirementsResponseDto }>()
);

export const saveSelectedCandidateDocumentSuccess = createAction(
    '[Compliance Report Effects] Save Selected Candidate Document Success'
);

export const markAsReviewedSuccess = createAction(
    '[Compliance Report Effects] Mark As Reviewed Success',
    props<{ contractId: number; complianceStatus: ComplianceStatusDto }>()
);

export const getJobRequirementsChangesSuccess = createAction(
    '[Compliance Report Component] Get Job Requirements Changes Success',
    props<{ jobRequirements: JobComplianceRequirementChanges | JobRequirementChangesDto }>()
);

// FAILURE ACTIONS
export const getCurrentContractFailure = createAction(
    '[Compliance Report Effects] Get Current Contract Fail',
    props<{ contractId: number; error: any }>()
);

export const processSpecialRulesFailure = createAction(
    '[Compliance Report Effects] Process Special rules Fail',
    props<{ error: any }>()
);

export const getCurrentPermissionsFailure = createAction(
    '[Compliance Report Effects] Get Current Permissions Fail',
    props<{ error: any }>()
);

export const updateRequirementsForCurrentContractFailure = createAction(
    '[Compliance Report Effects] Update Requirements For Current Contract Fail',
    props<{ error: any }>()
);

export const getAvailableStatusesFailure = createAction(
    '[Compliance Report Effects] Get Available Statuses Fail',
    props<{ error: any }>()
);

export const getReadinessStatusesFailure = createAction(
    '[Compliance Report Effects] Get Readiness Statuses Fail',
    props<{ error: any }>()
);

export const qualifyRequirementsFailure = createAction(
    '[Compliance Report Effects] Qualify Requirements Fail',
    props<{ error: any }>()
);

export const saveRequirementsFailure = createAction(
    '[Compliance Report Effects] Save Requirements Fail',
    props<{ error: any }>()
);

export const updateComplianceNoteForRequirementFailure = createAction(
    '[Compliance Report Effects] Update Compliance Note For Requirement Fail',
    props<{ contractId: number; error: any }>()
);

export const updateComplianceReportNoteFailure = createAction(
    '[Compliance Report Effects] Update Compliance Report Note Fail',
    props<{ contractId: number; error: any }>()
);

export const getAutomatchDataFailure = createAction(
    '[Compliance Report Effects] Get Automatch Data Fail',
    props<{ error: any }>()
);

export const saveSelectedCandidateDocumentFailure = createAction(
    '[Compliance Report Effects] Save Selected Candidate Document Fail',
    props<{ error: any }>()
);

export const getJobRequirementsChangesFailure = createAction(
    '[Compliance Report Component] Get Job Requirements Changes Fail',
    props<{ error: any }>()
);

export const saveRequirementsNotPerformed = createAction('[Compliance Report Effects] Save Requirements Not Performed');

export const markAsReviewedFailure = createAction(
    '[Compliance Report Effects] Mark As Reviewed Fail',
    props<{ error: any }>()
);

// CLEAR STATE ACTIONS
export const clearCurrentContract = createAction('[Compliance Report Component] Clear Current Contract');
export const clearAvailableStatuses = createAction('[Compliance Report Component] Clear Available Statuses');
export const clearReadinessStatuses = createAction('[Compliance Report Component] Clear Readiness Statuses');
export const clearModifiedRequirements = createAction('[Compliance Report Component] Clear Modified Requirements');
export const clearSelectedCandidateDocuments = createAction(
    '[Compliance Report Component] Clear Selected Candidate Documents'
);
export const clearCurrentPermissions = createAction('[Compliance Report Component] Clear Current Permissions');
export const clearAutomatchData = createAction('[Compliance Report Component] Clear Automatch Data');
export const clearManuallySelectedCandidateDocuments = createAction(
    '[Compliance Report Component] Clear Manually Selected Candidate Documents'
);
export const clearAllData = createAction('[Compliance Report Component] Clear All Data');
